import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';

import LoadingButton from '../../../components/buttons/Button';

import { CreateWorkflowTemplateGroupCERT } from '../../../redux/actions/certification/workflowTemplateGroupsActions';


const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyForm = {
  name: '',
  description: '',
  external_group_id: '',
  slug: '',
  is_globally_enabled: true,
  is_globally_visible: true,
};

const generateUuid = () => {
  // Prefer the browser's crypto.randomUUID when available; fall back to a
  // manual RFC-4122 v4 for older Safari / test envs.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const b = new Uint8Array(16);
  (crypto || window.msCrypto).getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
};


const CreateTemplateGroupModal = ({ open, setOpen, handleGet }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const handleClose = () => {
    setOpen(false);
    setFormData(emptyForm);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggle = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  const handleGenerateId = () => {
    setFormData((prev) => ({ ...prev, external_group_id: generateUuid() }));
  };

  const trimmedName = formData.name.trim();
  const trimmedId = formData.external_group_id.trim();
  const idIsValid = UUID_RE.test(trimmedId);
  const isValid = trimmedName.length > 0 && idIsValid;

  const handleSubmit = async () => {
    if (!isValid) return;
    const payload = {
      external_group_id: trimmedId,
      name: trimmedName,
      description: formData.description.trim(),
      slug: formData.slug.trim() || undefined,
      is_globally_enabled: formData.is_globally_enabled,
      is_globally_visible: formData.is_globally_visible,
    };
    const { statusCode } = await dispatch(CreateWorkflowTemplateGroupCERT(payload, setLoading));
    if (statusCode === 201) {
      handleGet && handleGet();
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="create-template-group-modal-title"
    >
      <Box
        className="flexCenterCenterRow"
        width={"100vw"}
        height={"100vh"}
      >
        <Box
          minWidth={"420px"}
          maxWidth={"600px"}
          width={"100%"}
          className="modal flex_SBColumn"
          sx={{ overflow: "hidden" }}
        >

          <Box sx={{ p: "20px 25px" }}>
            <Typography id="create-template-group-modal-title" variant='h6'>
              Create Workflow Template Group
            </Typography>
            <Typography variant='caption' sx={{ opacity: 0.7 }}>
              Add a new certification program (template group) to the local mirror. Once created it can be allotted to authority users from the Template Group Allotments page.
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

          <Box
            className="flex_CenterColumn"
            gap={"18px"}
            sx={{ margin: "25px" }}
          >

            <TextField
              label="Name"
              fullWidth
              required
              name="name"
              placeholder="e.g. DBE Certification"
              value={formData.name}
              onChange={handleChange}
            />

            <TextField
              label="Description"
              fullWidth
              name="description"
              multiline
              rows={2}
              placeholder="What is this certification program for?"
              value={formData.description}
              onChange={handleChange}
            />

            <TextField
              label="External Group ID (UUID)"
              fullWidth
              required
              name="external_group_id"
              placeholder="Paste the WorkflowTemplateGroup.id from the workflow service, or generate one"
              value={formData.external_group_id}
              onChange={handleChange}
              error={formData.external_group_id.length > 0 && !idIsValid}
              helperText={
                formData.external_group_id.length > 0 && !idIsValid
                  ? 'Must be a valid UUID (e.g. 2b013194-be8b-4887-9830-9d937ecd1997)'
                  : 'Uniquely identifies this group in the external workflow service — get this from GET /api/authority/public/groups/ on that service.'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Generate new UUID">
                      <IconButton size="small" onClick={handleGenerateId}>
                        <AutorenewOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Slug"
              fullWidth
              name="slug"
              placeholder="e.g. dbe-certification"
              value={formData.slug}
              onChange={handleChange}
            />

            <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_globally_enabled}
                    onChange={toggle('is_globally_enabled')}
                  />
                }
                label="Globally Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_globally_visible}
                    onChange={toggle('is_globally_visible')}
                  />
                }
                label="Globally Visible"
              />
            </Box>

          </Box>

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

          <Box
            className="flexCenterFERow"
            gap={"16px"}
            sx={{ p: "16px 20px" }}
          >
            <LoadingButton variant='text' onClick={handleClose}>
              Cancel
            </LoadingButton>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={!isValid}
              onClick={handleSubmit}
            >
              Create Group
            </LoadingButton>
          </Box>

        </Box>
      </Box>
    </Modal>
  );
};

export default CreateTemplateGroupModal;
